import { useToasts } from '@/components/common/Toasts';
import { logError } from '@/util/logger';
import { FetchResult } from '@apollo/client';
import { assert } from '@common/errors';
import { DEFAULT_RISK_SCORE, Goal } from '@common/goals';
import {
    CurrentRateInfo,
    UpdateGoalAccountOnboardingMutation,
    UpdateGoalAccountOnboardingMutationVariables,
} from '@common/graphql/generated';
import {
    getMyGoalAccount,
    getMyGoalOnboardingData,
    GoalOnboardingData,
    useUpsertMyGoalAccountOnboarding,
} from '@common/react/goals';
import { useCurrentUser, UserInfo } from '@common/react/user';
import { TOASTS } from '@common/toasts';
import {
    DefaultGoalAccountOnboardingInfo,
    GoalAccountOnboardingStep,
} from '@common/types';
import { createContext, useContext, useState } from 'react';
import { configByGoalType } from '../../config';
import {
    deriveTransactionSchedulePlan,
    DeriveTransactionSchedulePlanOptions,
    getGoalDataFromExistingGoal,
    getPartnerGoalAccountData,
    PartnerGoalAccountData,
} from '../../helpers';
import { useGoalDetailContext } from '../GoalDetailContext';
import { getIncludeTargetStep } from '../helpers';

type UpdateGoalOnboardingParams = Partial<{
    info: Partial<Omit<DefaultGoalAccountOnboardingInfo, 'infoType'>>;
    confirmedAt: UpdateGoalAccountOnboardingMutationVariables['confirmedAt'];
}>;

export type UpdateGoalOnboardingFcn = ({
    params,
    resetData,
}: {
    params: UpdateGoalOnboardingParams;
    resetData?: boolean;
}) => Promise<FetchResult<UpdateGoalAccountOnboardingMutation> | undefined>;

interface GoalOnboardingContext {
    goal: Goal;
    goalOnboardingData: GoalOnboardingData;
    updateGoalOnboarding: UpdateGoalOnboardingFcn;
    updateGoalOnboardingData: (
        data: Partial<GoalOnboardingData>,
        options?: DeriveTransactionSchedulePlanOptions & {
            newGoal?: Goal;
        }
    ) => void;
    resetGoalOnboardingData: (goal: Goal) => void;
    isUpdating: boolean;
    step: GoalAccountOnboardingStep;
    setStep: (position: GoalAccountOnboardingStep) => void;
    onClose: () => void;
    /** If partner already has a goal account, or has a *confirmed* goal account
     * onboarding, propagate that information separately so that UI can
     * concretely indicate "this is what your partner is actually doing" vs.
     * "this is what you are planning for your partner to do"
     */
    partnerGoalAccountData?: PartnerGoalAccountData;
}

const Ctx = createContext<GoalOnboardingContext | null>(null);

export const GoalOnboardingProvider = ({
    goalUnfiltered,
    children,
}: {
    goalUnfiltered: Goal;
    children: React.ReactNode;
}) => {
    const config = configByGoalType[goalUnfiltered.type];
    const { showErrorToast } = useToasts();
    const [isUpdating, setIsUpdating] = useState(false);
    const { currentRateInfo, onClose } = useGoalDetailContext();
    const { userInfo } = useCurrentUser();
    const [step, setStep] = useState(getInitialStep(goalUnfiltered));
    const partnerGoalAccountData = getPartnerGoalAccountData(
        goalUnfiltered,
        currentRateInfo
    );

    const [goalOnboardingData, setGoalOnboardingData] = useState(
        getInitialGoalOnboardingData({
            goal: goalUnfiltered,
            userInfo,
            currentRateInfo,
        })
    );
    const resetGoalOnboardingData = (goal: Goal) =>
        setGoalOnboardingData(
            getInitialGoalOnboardingData({
                goal,
                userInfo,
                currentRateInfo,
            })
        );
    const upsertMyGoalAccountOnboarding =
        useUpsertMyGoalAccountOnboarding(goalUnfiltered);

    // Persist updates to the current user's goal onboarding record.
    const updateGoalOnboarding: UpdateGoalOnboardingFcn = async ({
        params,
        resetData,
    }) => {
        setIsUpdating(true);
        const previousGoalOnboardingData = goalOnboardingData;
        // Optimistically update
        setGoalOnboardingData({
            ...goalOnboardingData,
            ...params.info,
            ...('confirmedAt' in params
                ? { isConfirmed: !!params.confirmedAt }
                : {}),
        });

        try {
            const res = await upsertMyGoalAccountOnboarding(params);

            // If resetData is true, reset goal onboarding data derived from the
            // updated goal.
            if (resetData) {
                const maybeGoal = res?.data?.updateGoalAccountOnboarding?.goal;
                const updatedGoal = maybeGoal ? (maybeGoal as Goal) : undefined;
                if (updatedGoal) {
                    resetGoalOnboardingData(updatedGoal);
                } else {
                    logError(
                        new Error(
                            'Failed to reset goal onboarding data after updating goal account onboarding - goal missing'
                        )
                    );
                }
            }

            setIsUpdating(false);
            return res;
        } catch {
            // Rollback optimistic update on error
            setGoalOnboardingData(previousGoalOnboardingData);
            showErrorToast(TOASTS.updateGoalError);
            setIsUpdating(false);
        }
    };

    // Update current user's goal onboarding data locally, *without* persisting.
    // Useful for within-page interactions like the GoalOnboardingPlan inputs.
    const updateGoalOnboardingData = (
        data: Partial<GoalOnboardingData>,
        options?: DeriveTransactionSchedulePlanOptions & {
            newGoal?: Goal;
        }
    ) => {
        const newData = {
            ...goalOnboardingData,
            ...data,
        };

        // Ensure we're using updated annual return
        const annualReturn = config.getAnnualReturn({
            investmentType:
                newData.riskScore === 0
                    ? 'HIGH_YIELD_CASH'
                    : newData.investmentType,
            riskScore: newData.riskScore,
            currentRateInfo,
        });

        const fullReset =
            // If initialDepositPlan is being updated, and it changed from zero to
            // non-zero or vice-versa, re-coerce the transaction schedule date to
            // ensure it's appropriately offset, if necessary.
            (newData.initialDepositPlan === 0 &&
                goalOnboardingData.initialDepositPlan > 0) ||
            (newData.initialDepositPlan > 0 &&
                goalOnboardingData.initialDepositPlan === 0) ||
            // If user changed recurrence, reset everything.
            newData.transactionSchedulePlan.recurrence.freq !==
                goalOnboardingData.transactionSchedulePlan.recurrence.freq ||
            newData.transactionSchedulePlan.recurrence.interval !==
                goalOnboardingData.transactionSchedulePlan.recurrence.interval;

        const resetStartDate = options?.resetStartDate ?? fullReset;

        const clampToTargetAmount =
            options?.clampToTargetAmount ??
            (fullReset ||
                // If user updated startDate, just clamp to target amount.
                newData.transactionSchedulePlan.startDate !==
                    goalOnboardingData.transactionSchedulePlan.startDate);

        newData.transactionSchedulePlan = deriveTransactionSchedulePlan(
            {
                ...newData,
                goal: options?.newGoal ?? goalUnfiltered,
                annualReturn,
                currentRateInfo,
            },
            { resetStartDate, clampToTargetAmount }
        );

        setGoalOnboardingData(newData);
    };

    return (
        <Ctx.Provider
            value={{
                goal: goalUnfiltered,
                goalOnboardingData,
                updateGoalOnboarding,
                updateGoalOnboardingData,
                resetGoalOnboardingData,
                isUpdating,
                onClose,
                step,
                setStep,
                partnerGoalAccountData,
            }}
        >
            {children}
        </Ctx.Provider>
    );
};

export const useGoalOnboardingContext = (): GoalOnboardingContext => {
    const ctx = useContext(Ctx);
    assert(ctx);
    return ctx;
};

// helpers

export const getInitialStep = (goal: Goal): GoalAccountOnboardingStep => {
    // If current user already has a goal account, onboarding is complete
    if (getMyGoalAccount(goal)) return 'COMPLETE';

    const { lastCompletedStep, isConfirmed } = getMyGoalOnboardingData(goal);

    const includeTargetStep = getIncludeTargetStep(goal.type);

    // If goal is already confirmed, show the confirm view
    if (isConfirmed) return 'CONFIRM';

    // If last completed step is present, return appropriate following step.
    if (lastCompletedStep === 'ACCOUNT_TYPE') {
        return includeTargetStep ? 'TARGET' : 'PORTFOLIO';
    }
    if (lastCompletedStep === 'TARGET') return 'PORTFOLIO';
    if (lastCompletedStep === 'PORTFOLIO') return 'PLAN';
    if (lastCompletedStep === 'PLAN') return 'CONNECT';
    if (lastCompletedStep === 'CONNECT') return 'CONFIRM';

    // If last completed step is INTRO or empty, return correct default step:
    // - If member is the goal creator, default to the account type view
    // - Otherwise, default to the portfolio view
    return goal.isMine ? 'ACCOUNT_TYPE' : 'PORTFOLIO';
};

const getInitialGoalOnboardingData = ({
    goal,
    userInfo,
    currentRateInfo,
}: {
    goal: Goal;
    userInfo: UserInfo;
    currentRateInfo: CurrentRateInfo;
}): GoalOnboardingData => {
    const config = configByGoalType[goal.type];
    const goalData = getGoalDataFromExistingGoal(goal);
    const recommendedRiskScore = config.getRecommendedRiskScore({
        goalData,
        userInfo,
    });
    const myGoalOnboardingInfo = getMyGoalOnboardingData(goal);
    const riskScore =
        myGoalOnboardingInfo.riskScore ??
        recommendedRiskScore ??
        // TODO: Try to remove this
        DEFAULT_RISK_SCORE;
    const annualReturn = config.getAnnualReturn({
        investmentType:
            riskScore === 0
                ? 'HIGH_YIELD_CASH'
                : myGoalOnboardingInfo.investmentType,
        riskScore,
        currentRateInfo,
    });
    const initialDepositPlan = myGoalOnboardingInfo.initialDepositPlan ?? 0;

    // If goal is in "confirmed" state, don't recalculate the transaction
    // schedule plan.
    const transactionSchedulePlan =
        myGoalOnboardingInfo.transactionSchedulePlan &&
        myGoalOnboardingInfo.isConfirmed
            ? myGoalOnboardingInfo.transactionSchedulePlan
            : // NOTE: deriveTransactionSchedulePlan uses getPartnerGoalAccountData
              // under the hood, so the inputs match partnerGoalAccountData
              // from GoalOnboardingContext exactly.
              // TODO: Consider explicitly passing partnerGoalAccountData as a
              // param to make this logic less brittle.
              deriveTransactionSchedulePlan(
                  {
                      goal,
                      annualReturn,
                      initialDepositPlan,
                      transactionSchedulePlan:
                          myGoalOnboardingInfo.transactionSchedulePlan,
                      currentRateInfo,
                  },
                  { resetStartDate: true, clampToTargetAmount: true }
              );

    return {
        riskScore,
        initialDepositPlan,
        transactionSchedulePlan,
        partnerInitialDepositPlan:
            myGoalOnboardingInfo.partnerInitialDepositPlan,
        partnerTransactionSchedulePlan:
            myGoalOnboardingInfo.partnerTransactionSchedulePlan,
        lastCompletedStep: myGoalOnboardingInfo.lastCompletedStep,
        fundingAccountId: myGoalOnboardingInfo.fundingAccountId,
        isConfirmed: myGoalOnboardingInfo.isConfirmed,
        investmentType: myGoalOnboardingInfo.investmentType,
    };
};
